/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { ImportUsersConsts } from 'client/util/Constants';
import { extend, mixin } from 'lodash';
import CollectionModule from 'store/modules/CollectionModule';
import ImportUsersMixin, { updateState } from 'client/store/mixins/ImportUsersMixin';
import WorkdayWorkerApi from 'client/api/v2/WorkdayWorkerApi';

const { source } = ImportUsersConsts;

export const processUsers = users => users.map((user) => {
  const systemUser = {
    email: user.email,
    firstname: user.firstName,
    lastname: user.lastName,
    username: user.username,
  };

  let employeeId;
  let workdayId;

  if (user.attributes) {
    employeeId = user.attributes.employee_id;
    workdayId = user.attributes.WorkdayID;
  }

  if (employeeId || workdayId) {
    systemUser.attributes = [];
  }

  if (employeeId) {
    systemUser.attributes.push({ name: 'EmployeeID', value: employeeId });
  }

  if (workdayId) {
    systemUser.attributes.push({ name: 'WorkdayID', value: workdayId });
  }

  return systemUser;
});

export class WorkdayWorkerCollectionClass extends CollectionModule {
  /**
   * Override method from base class to add workday specific fields to the Vuex
   * module entities and features.
   */
  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, ImportUsersMixin.actions);
    mixin(module.getters, ImportUsersMixin.getters);
    mixin(module.mutations, ImportUsersMixin.mutations);

    module.actions.fetchCollection = (...params) => this.fetchCollection(...params);
    module.actions.importSelectedUsers = (...params) => this.importSelectedUsers(...params);
    module.actions.setBulkImportLimit = (...params) => this.setBulkImportLimit(...params);
    module.actions.setWorkdayId = (...params) => this.setWorkdayId(...params);

    module.getters.getErrorPollingImportResults = (...params) => this
      .getErrorPollingImportResults(...params);

    module.mutations.SET_BULK_IMPORT_LIMIT = (...params) => this.SET_BULK_IMPORT_LIMIT(...params);
    module.mutations.SET_WORKDAY_ID = (...params) => this.SET_WORKDAY_ID(...params);

    return module;
  }

  // ----------------------------------------------------------------------------------------------
  // State
  // ----------------------------------------------------------------------------------------------

  getInitialState() {
    const state = updateState(super.getInitialState());
    state.entities.workdayId = '';
    return state;
  }

  // ----------------------------------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------------------------------

  /**
   * Override method from ImportUsersMixin since the WorkdayWorkerApi must be called to fetch users
   * from Workday.
   */
  fetchCollection(context, { next = false } = {}) {
    return super.fetchCollection(context, { next });
  }

  /**
   * Override method from ImportUsersMixin to specify a custom method to process the users prior to
   * importing them into JumpCloud.
   */
  importSelectedUsers(context) {
    return ImportUsersMixin.actions.importSelectedUsers(context, processUsers, source.workday);
  }

  setBulkImportLimit({ commit }, bulkImportLimit) {
    commit('SET_BULK_IMPORT_LIMIT', bulkImportLimit);
  }

  setWorkdayId({ commit }, { workdayId }) {
    commit('SET_WORKDAY_ID', { workdayId });
  }

  // ----------------------------------------------------------------------------------------------
  // Getters
  // ----------------------------------------------------------------------------------------------

  getErrorPollingImportResults(state = { features: {} }) {
    return state.features.errorPollingImportResults;
  }

  // ----------------------------------------------------------------------------------------------
  // Mutations
  // ----------------------------------------------------------------------------------------------

  /**
   * When sorting or searching a soft reset is performed. This preserves the
   * selected and modified items. This allows the admin user's selection and
   * changes to persist, and not be lost, when searching and sorting.
   */
  RESET_COLLECTION(state = {
    features: {},
    entities: {},
  }, { soft = false } = {}) {
    super.RESET_COLLECTION(state, { soft });
    const initialState = this.getInitialState();
    state.entities.workdayId = initialState.entities.workdayId;
    state.features.jobId = initialState.features.jobId;
  }

  SET_BULK_IMPORT_LIMIT(state = { features: {} }, bulkImportLimit = false) {
    state.features.bulkImportLimit = bulkImportLimit;
  }

  /**
   * Override method from base class to merge changes made to modified users
   * into the entities so that the admin user's changes to Workday workers
   * persist when sorting.
   */
  SET_COLLECTION(state = { entities: { boundItems: { byId: {} } } }, items = []) {
    super.SET_COLLECTION(state, items);

    Object.keys(state.entities.boundItems.byId).forEach((id) => {
      if (state.entities.items.byId[id]) {
        extend(
          state.entities.items.byId[id],
          state.entities.boundItems.byId[id],
        );
      }
    });
  }

  /**
   * @todo Can this be be set on the API initially rather than passed around as
   * needed? To do so implies that instead of exporting class instances as a
   * singleton from our Api classes that we instead will instantiate Api class
   * instances as needed.
   */
  SET_WORKDAY_ID(state = { entities: { workdayId: {} } }, { workdayId }) {
    state.entities.workdayId = workdayId;
    this.Api.setWorkdayId(workdayId);
  }
}

export default new WorkdayWorkerCollectionClass(WorkdayWorkerApi, 'id', '').extractModule();
